/**
 * usePermissions - Central hook for permission checking
 *
 * Reads the current user's permissions from AuthProvider (fetched via /api/v1/me)
 * and provides helper functions to check if the user can perform specific actions.
 *
 * Permissions are cached in AuthProvider state until page refresh (no TTL).
 * The backend is the source of truth and enforces permissions on every API call.
 * This hook is for UX only (disable buttons, hide nav items, show 403 pages).
 */
import { useAuth } from '@/providers/AuthProvider';

interface Permission {
  resource: string;
  action: string;
}

interface PermissionHelpers {
  /** Check if user has permission for a specific resource + action */
  can: (resource: string, action: string) => boolean;
  /** Check if user has ANY of the given permissions */
  canAny: (checks: Permission[]) => boolean;
  /** Check if user has ALL of the given permissions */
  canAll: (checks: Permission[]) => boolean;
  /** Whether the user is an admin */
  isAdmin: boolean;
  /** Whether permissions are still loading */
  isLoading: boolean;
}

/**
 * Check if a permission map grants access for a resource + action.
 *
 * Mirrors the backend's EffectivePermissions.checkGlobal() logic:
 * 1. Wildcard resource with wildcard action → allowed
 * 2. Wildcard resource with specific action → allowed
 * 3. Specific resource with wildcard action → allowed
 * 4. Specific resource with specific action → allowed
 *
 * Exported for the module-level mirror in lib/auth/currentPermissions.ts —
 * code outside the React tree must share this exact logic, not fork it.
 */
export function checkPermission(
  global: Record<string, string[]> | undefined,
  resource: string,
  action: string
): boolean {
  if (!global) return false;

  // Check wildcard resource
  const wildcardActions = global['*'];
  if (wildcardActions) {
    if (wildcardActions.includes('*') || wildcardActions.includes(action)) {
      return true;
    }
  }

  // Check specific resource
  const resourceActions = global[resource];
  if (resourceActions) {
    if (resourceActions.includes('*') || resourceActions.includes(action)) {
      return true;
    }
  }

  return false;
}

export function usePermissions(): PermissionHelpers {
  const { user, isLoading } = useAuth();

  const permissions = user?.permissions;
  const isAdmin = permissions?.isAdmin === true || permissions?.isSuperAdmin === true;

  const can = (resource: string, action: string): boolean => {
    if (isLoading) return false;
    if (!user) return false;
    if (isAdmin) return true;
    return checkPermission(permissions?.global, resource, action);
  };

  const canAny = (checks: Permission[]): boolean => {
    return checks.some((check) => can(check.resource, check.action));
  };

  const canAll = (checks: Permission[]): boolean => {
    return checks.every((check) => can(check.resource, check.action));
  };

  return { can, canAny, canAll, isAdmin, isLoading };
}
